import React, { useEffect, useState } from 'react';
import { api, useServer } from '../contexts/ServerContext';
import { Cat, Clock9 } from 'lucide-react';
import { steamId64ToV2, getCachedAvatar, getCachedDisplayName } from '../utils/helpers';

function PvpLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [avatars, setAvatars] = useState({});
  const [names, setNames] = useState({});
  const pageSize = 20;
  const { server } = useServer();

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  useEffect(() => {
    fetchLogs();
  }, [currentPage, server]);

  useEffect(() => {
    const ids = Array.from(new Set([
      ...logs.map(l => l.instigator),
      ...logs.map(l => l.victim)
    ].filter(Boolean)));
    ids.forEach(steamid64 => {
      const v2 = steamId64ToV2(steamid64);
      if (!names[v2]) {
        getCachedDisplayName(steamid64, async () => {
          const resp = await api.get(`/api/players/${v2}`);
          return resp.data.displayName || resp.data.name || v2;
        }).then(name => {
          setNames(prev => ({ ...prev, [v2]: name }));
        }).catch(() => {
          setNames(prev => ({ ...prev, [v2]: v2 }));
        });
      }
      if (!avatars[steamid64]) {
        getCachedAvatar(steamid64, async () => {
          const resp = await api.get(`/api/steam/avatar/${steamid64}`);
          return resp.data.avatar;
        }).then(url => {
          setAvatars(prev => ({ ...prev, [steamid64]: url }));
        }).catch(() => {
          setAvatars(prev => ({ ...prev, [steamid64]: null }));
        });
      }
    });
  }, [logs, server]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      console.log('Загружаем PVP логи для сервера:', server);
      const response = await api.get(`/api/pvp-logs?page=${currentPage}&limit=${pageSize}&server=${server}`);
      console.log('PVP логи получены:', response.data);
      setLogs(response.data.logs);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Ошибка загрузки PVP логов:', error);
      console.error('Детали ошибки:', error.response?.data);
      setLogs([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const filtered = logs?.filter(l => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (l.instigator && l.instigator.toLowerCase().includes(q)) ||
      (l.victim && l.victim.toLowerCase().includes(q))
    );
  }) || [];
  const paginated = filtered;

  function formatTime(ts) {
    if (!ts) return '-';
    const d = new Date(ts * 1000);
    return d.toLocaleString('ru-RU');
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gmod-white ml-4">PVP-Логи</h1>
        <p className="text-gmod-white opacity-60 mt-2 ml-4">Список всех PVP-событий на сервере</p>
      </div>
      <div className="mb-4">
        <input
          type="text"
          value={search}
          onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
          placeholder="Поиск по SteamID..."
          className="w-full px-4 py-2 bg-gmod-lighter border border-gmod-lighterer rounded-md text-gmod-white placeholder-gmod-white placeholder-opacity-40 focus:outline-none focus:ring-2 focus:ring-theme-primary"
        />
      </div>
      <div className="bg-gmod-dark rounded-lg border border-gmod-lighterer p-6">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-primary"></div>
          </div>
        ) : paginated.length === 0 ? (
          <p className="text-gmod-white opacity-60 text-center py-8">Логи не найдены</p>
        ) : (
          <>
            <table className="min-w-full text-sm text-gmod-white opacity-80">
              <thead className="sticky top-0 bg-gmod-dark z-10">
                <tr>
                  <th className="px-4 py-2 text-left">Кто</th>
                  <th className="px-4 py-2 text-left">Кому</th>
                  <th className="px-4 py-2 text-center align-middle">
                    <span className="inline-flex items-center justify-center">
                      <Clock9 className="h-6 w-6 text-gmod-white opacity-60 mr-1" />
                      Время
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((l, i) => {
                  const recent = l.timestamp && (Date.now()/1000 - l.timestamp < 60*60*6); // 6 часов
                  const instigatorV2 = steamId64ToV2(l.instigator);
                  const victimV2 = steamId64ToV2(l.victim);
                  return (
                    <tr key={i} className={`border-b border-gmod-lighterer hover:bg-theme-primary/40 transition ${recent ? 'bg-gmod-lighter/20' : ''}`}>
                      <td className="px-4 py-2 text-left">
                        <span className="flex items-center gap-2">
                          {l.avatar ? (
                            <img
                              src={l.avatar}
                              alt=""
                              className="w-8 h-8 min-w-[32px] rounded-full border-2 border-theme-primary"
                            />
                          ) : (
                            <Cat className="w-8 h-8 min-w-[32px] text-gmod-white opacity-40" />
                          )}
                          <div className="flex flex-col">
                            <a
                              href={`/players/${instigatorV2}`}
                              className="text-theme-primary font-semibold hover:underline truncate max-w-[120px]"
                            >
                              {names[instigatorV2] || instigatorV2}
                            </a>
                            <span
                              className="block text-xs text-gmod-white opacity-60 font-mono cursor-pointer hover:text-theme-primary"
                              onClick={() => {
                                navigator.clipboard.writeText(instigatorV2);
                                setSearch(instigatorV2);
                                setCurrentPage(1);
                              }}
                            >
                              {instigatorV2}
                            </span>
                          </div>
                        </span>
                      </td>
                      <td className="px-4 py-2 text-left">
                        <span className="flex items-center gap-2">
                          {l.avatarVictim ? (
                            <img
                              src={l.avatarVictim}
                              alt=""
                              className="w-8 h-8 min-w-[32px] rounded-full border-2 border-theme-primary"
                            />
                          ) : (
                            <Cat className="w-8 h-8 min-w-[32px] text-gmod-white opacity-40" />
                          )}
                          <div className="flex flex-col">
                            <a
                              href={`/players/${victimV2}`}
                              className="text-theme-primary font-semibold hover:underline truncate max-w-[120px]"
                            >
                              {names[victimV2] || victimV2}
                            </a>
                            <span
                              className="block text-xs text-gmod-white opacity-60 font-mono cursor-pointer hover:text-theme-primary"
                              onClick={() => {
                                navigator.clipboard.writeText(victimV2);
                                setSearch(victimV2);
                                setCurrentPage(1);
                              }}
                            >
                              {victimV2}
                            </span>
                          </div>
                        </span>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <span className="inline-flex items-center gap-1">
                          <Clock9 className="h-5 w-5 text-gmod-yellow" />
                          <span className="text-gmod-white text-sm font-semibold">{formatTime(l.timestamp)}</span>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="flex justify-center space-x-2 mt-4">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-gmod-lighter text-gmod-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gmod-lighterer"
                >
                  Назад
                </button>
                <span className="px-4 py-2 text-gmod-white">
                  {currentPage} из {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-gmod-lighter text-gmod-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gmod-lighterer"
                >
                  Вперёд
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default PvpLogs; 